import "@preact/signals";
import * as preact from "preact";

import * as readlangLocalStorage from "readlangLocalStorage";
import * as dateUtils from "shared/dateUtils";
import * as languageMap from "shared/languageMap";
import { cn, sleep } from "shared/utils";
import stores from "stores/stores";
import StreakStore from "stores/StreakStore";
import * as streakReminderPrompt from "streakReminderPrompt";
import * as streakRepair from "streakRepair";
import { ProgressRing } from "svgUtils";
import * as buttonRole from "ui/buttonRole";
import Popover from "ui/Popover";
import * as _ from "underscore";
import { EmailReminderAndStreakRepair } from "views/EmailReminderAndStreakRepairView";

const FireIcon = ({ completed }: { completed: boolean }) => (
  <svg
    class={`fireIcon${completed ? " metGoal" : ""}`}
    width="13"
    height="16"
    viewBox="0 0 13 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clip-path="url(#clip0_5_21)">
      <g clip-path="url(#clip1_5_21)">
        <path
          d="M4.62254 0.168746C4.84888 -0.0593792 5.2 -0.0562542 5.42634 0.171871C6.22723 0.981246 6.97879 1.85312 7.68103 2.79687C8.00022 2.34687 8.36295 1.85625 8.75469 1.45625C8.98393 1.225 9.33795 1.225 9.56719 1.45937C10.5712 2.49062 11.4214 3.85312 12.0192 5.14687C12.6083 6.42187 13 7.725 13 8.64375C13 12.6312 10.104 16 6.5 16C2.85536 16 0 12.6281 0 8.64062C0 7.44062 0.516518 5.975 1.31741 4.525C2.12701 3.05312 3.27031 1.51875 4.62254 0.168746ZM6.54933 13C7.28348 13 7.93348 12.7812 8.54576 12.3437C9.76741 11.425 10.0953 9.5875 9.36116 8.14375C9.23058 7.8625 8.89687 7.84375 8.70826 8.08125L7.97701 8.99687C7.78549 9.23437 7.44018 9.22812 7.26027 8.98125C6.78147 8.325 5.92545 7.15312 5.43795 6.4875C5.25513 6.2375 4.90692 6.23437 4.72121 6.48437C3.7404 7.8125 3.2471 8.65 3.2471 9.59062C3.25 11.7312 4.7183 13 6.54933 13Z"
          fill="currentColor"
        />
      </g>
    </g>
    <defs>
      <clipPath id="clip0_5_21">
        <rect width="13" height="16" fill="currentColor" />
      </clipPath>
      <clipPath id="clip1_5_21">
        <rect width="13" height="16" fill="currentColor" />
      </clipPath>
    </defs>
  </svg>
);

interface Props {
  store: StreakStore;
  hideUponCompletion?: boolean;
  /**
   * Open the popover as the reminderReprompt2 prompt: a congratulation, the
   * reminder switch and an arrow to the indicator (streakReminderPrompt.ts).
   */
  reminderPrompt?: boolean;
  onReminderPromptClose?: () => void;
}

/** The popover open: how it came to be */
interface Opened {
  /** Opened unasked, as a lost streak can still be repaired */
  autoShow: boolean;
  reminderPrompt: boolean;
}

interface State {
  fadeOut: boolean;
  opened?: Opened;
}

/** The indicator of the streak and today's goal, and its popover */
class StreakView extends preact.Component<Props, State> {
  state: State = { fadeOut: false };

  constructor(props: Props) {
    super(props);
    void stores.streak.refetchIfAppropriate();
  }

  componentDidMount() {
    if (this.props.reminderPrompt) this.showReminderPrompt();
  }

  async componentDidUpdate(previousProps: Props) {
    if (this.props.reminderPrompt && !previousProps.reminderPrompt) {
      this.showReminderPrompt();
    }
    const todayStats = this.props.store.todayStats;
    if (todayStats?.goalMet && this.props.hideUponCompletion) {
      await sleep(5000);
      this.setState({ fadeOut: true });
    }
    this.autoShowPopoverIfAppropriate();
  }

  showReminderPrompt() {
    this.setState({ opened: { autoShow: false, reminderPrompt: true } });
    streakReminderPrompt.trackShown();
  }

  async autoShowPopoverIfAppropriate() {
    const repairableDays = stores.streak.repairableDays ?? 0;
    if (repairableDays < streakRepair.minimumDaysToNudge) return;
    if (await this.haveAutoShownTooltipTodayAndUpdate()) return;
    this.setState({ opened: { autoShow: true, reminderPrompt: false } });
  }
  async haveAutoShownTooltipTodayAndUpdate() {
    const todayDate = new Date();
    const todayDayString = dateUtils.serializeDay(
      dateUtils.dateToDay(todayDate, dateUtils.getTimezoneOffset(todayDate))
    );
    const lastAutoShowTooltipDay = await new Promise((resolve) =>
      readlangLocalStorage.getItem("lastAutoShowTooltipDay", resolve)
    );
    await new Promise((resolve: any) =>
      readlangLocalStorage.setItem(
        "lastAutoShowTooltipDay",
        todayDayString,
        resolve
      )
    );
    if (!lastAutoShowTooltipDay) return false;
    if (lastAutoShowTooltipDay === todayDayString) return true;
    return false;
  }

  close = () => {
    if (this.state.opened?.reminderPrompt) {
      streakReminderPrompt.trackClosed();
      this.props.onReminderPromptClose?.();
    }
    this.setState({ opened: undefined });
  };

  toggle = (event: MouseEvent) => {
    event.preventDefault();
    if (this.state.opened) {
      this.close();
    } else {
      this.setState({ opened: { autoShow: false, reminderPrompt: false } });
    }
  };

  dismissNotification = async () => {
    if (
      confirm(
        "This message and the red dot will disappear automatically once you reach your daily goal. Are you sure you want to delete it now?"
      )
    ) {
      await stores.storedNotifications.deleteAllStoredNotifications();
      this.close();
    }
  };

  /** Today's words, by language once there are two */
  renderTodayStats() {
    const todayStats = this.props.store.todayStats;
    const activeLanguages = Object.entries(todayStats.byLanguage ?? {})
      .filter(([, stats]) => stats.wordsRead > 0 || stats.wordsPracticed > 0)
      .sort(
        ([, a], [, b]) =>
          b.wordsRead + b.wordsPracticed - (a.wordsRead + a.wordsPracticed)
      );
    if (activeLanguages.length < 2) {
      return (
        <ul>
          <li>Words read: {todayStats.wordsRead}</li>
          <li>Words practiced: {todayStats.wordsPracticed}</li>
        </ul>
      );
    }
    return (
      <>
        <ul class="byLanguage">
          {activeLanguages.map(([code, stats]) => {
            const parts = [];
            if (stats.wordsRead > 0) parts.push(`${stats.wordsRead} read`);
            if (stats.wordsPracticed > 0)
              parts.push(`${stats.wordsPracticed} practiced`);
            return (
              <li key={code}>
                {languageMap.map[code]?.english ?? code}: {parts.join(", ")}
              </li>
            );
          })}
        </ul>
        <ul class="totals">
          <li>Total words read: {todayStats.wordsRead}</li>
          <li>Total words practiced: {todayStats.wordsPracticed}</li>
        </ul>
      </>
    );
  }

  renderPopover({ autoShow, reminderPrompt }: Opened) {
    const days = this.props.store.days;
    const lastNotification = _.last(
      stores.storedNotifications?.notifications.value ?? []
    );
    return (
      <>
        <h4>{reminderPrompt ? "Nicely done!" : `${days} day streak`}</h4>
        <p>
          {reminderPrompt
            ? days === 1
              ? "You've met today's goal and started a new streak."
              : `You've met today's goal: that's a ${days} day streak.`
            : this.props.store.todayStats.fractionOfGoalMet >= 1
              ? "Today's goal met!"
              : "Continue reading or practicing to meet today's goal!"}
        </p>
        {reminderPrompt ? (
          // Unrequested, so make it obvious it can be dismissed
          <span
            {...buttonRole.props}
            class="closePromptButton icon-remove"
            aria-label="Close"
            onClick={this.close}
          ></span>
        ) : (
          this.renderTodayStats()
        )}
        <EmailReminderAndStreakRepair
          stores={stores}
          autoShow={autoShow}
          reminderOptInOnly={reminderPrompt}
        />
        {lastNotification && !reminderPrompt ? (
          <div class="redDotNotice">
            <span
              {...buttonRole.props}
              class="dismissMessageButton icon-remove"
              aria-label="Dismiss"
              onClick={this.dismissNotification}
            ></span>
            <p>
              <span class="redDot"></span>
              {lastNotification.body}
            </p>
          </div>
        ) : null}
      </>
    );
  }

  render() {
    if (!this.props.store.hasData) return null;

    const todayStats = this.props.store.todayStats;
    const showRedDot = stores.storedNotifications?.hasNotifications;
    const outerWidth = 16;
    // Derived rather than latched: the reminder prompt, which points at the
    // indicator, lifts hideUponCompletion while it's open even if the fade
    // timer has already fired
    const fadeOut = this.state.fadeOut && this.props.hideUponCompletion;
    const svgArc = todayStats.goalMet ? (
      <div
        style={{
          display: "block",
          width: outerWidth * 2,
          height: outerWidth * 2,
        }}
      ></div>
    ) : (
      <div>
        <ProgressRing
          innerRadius={13.7}
          outerRadius={outerWidth}
          arcAngle={Math.min(1, todayStats.fractionOfGoalMet) * 360}
        />
      </div>
    );
    const { opened } = this.state;
    const indicator = (
      <div
        {...buttonRole.props}
        class={cn({
          streakView: true,
          metGoal: todayStats.goalMet,
          fadeOut,
        })}
        aria-label={`${this.props.store.days} day streak`}
        aria-expanded={Boolean(opened)}
        onClick={this.toggle}
        style={
          fadeOut
            ? {
                opacity: 0,
                width: 0,
                margin: 0,
                transition: "opacity 0.5s, width 0.5s, margin 0.5s",
              }
            : {}
        }
      >
        <div class="image">
          {svgArc}
          <span>
            <FireIcon completed={todayStats.goalMet} />
          </span>
          {showRedDot ? <div class="redDot"></div> : null}
        </div>
        <div class={cn({ days: true, includesToday: todayStats.goalMet })}>
          {this.props.store.days}
        </div>
      </div>
    );
    return (
      <Popover
        class="streakPopoverAnchor"
        popoverClass={cn({
          streakPopover: true,
          reminderPrompt: opened?.reminderPrompt,
        })}
        role="dialog"
        aria-label={opened?.reminderPrompt ? "Nicely done!" : "Your streak"}
        open={Boolean(opened)}
        onClose={this.close}
        arrow={opened && (opened.reminderPrompt || opened.autoShow)}
        anchor={indicator}
      >
        {opened ? this.renderPopover(opened) : null}
      </Popover>
    );
  }
}

export default StreakView;
